/**
 * useMoney — currency-aware money formatting, the mirror of useUnits for the
 * money dimension.
 *
 * A money value is `{ amount, currency }`. By default it renders in its own
 * native currency. When the user picks a display currency (Settings), amounts
 * convert via the FX rate valid *as of the value's own date* (effective-dated,
 * from Oxbo-M `/v1/fx/rate`). Demo data is all USD so the native path is the
 * norm; the FX path is wired for mixed-currency data.
 *
 * No implicit currency: a value with no currency label shows its amount but is
 * never silently assumed to be USD.
 */
import { useCallback } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { intlTag } from "@lib/format";
import { store } from "@app/store";
import { unitsApi } from "./unitsApi";
import { applyFx } from "./convert";

export interface MoneyFormatter {
  /** The display currency in effect, or null when showing native currencies. */
  displayCurrency: string | null;
  /**
   * Format a money amount. `native` is the value's own currency; `asOf` is the
   * value's date (for the effective-dated FX lookup). When a display currency is
   * set and differs from native, converts via FX as-of that date; if no rate is
   * available it falls back to the native currency rather than coercing 1:1.
   */
  money: (
    amount: number | null | undefined,
    native: string | null | undefined,
    opts?: { compact?: boolean; asOf?: string },
  ) => string;
}

function formatAmount(
  amount: number,
  currency: string | undefined,
  tag: string,
  compact: boolean,
): string {
  if (!currency) {
    // No-currency defect: show the number, never a fake currency symbol.
    return amount.toLocaleString(tag, {
      notation: compact ? "compact" : "standard",
      maximumFractionDigits: compact ? 2 : 0,
    });
  }
  return amount.toLocaleString(tag, {
    style: "currency",
    currency,
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 2 : 0,
  });
}

/**
 * Read a cached FX rate synchronously and, if missing, kick off a fetch so the
 * next render has it. Returns null when no rate is (yet) available — the caller
 * then shows the native currency, never a coerced 1:1 conversion.
 */
function fxRate(base: string, quote: string, asOf: string): number | null {
  const selector = unitsApi.endpoints.getFxRate.select({ base, quote, asOf });
  const entry = selector(store.getState());
  if (entry.data) return entry.data.rate;
  if (!entry.isLoading && !entry.isError && entry.status === "uninitialized") {
    // Warm the cache for subsequent renders; ignore the promise here.
    void store.dispatch(unitsApi.endpoints.getFxRate.initiate({ base, quote, asOf }));
  }
  return null;
}

export function useMoney(): MoneyFormatter {
  const { locale } = useLocale();
  const displayCurrency = useAppSelector((s) => s.prefs.displayCurrency);

  const money = useCallback<MoneyFormatter["money"]>(
    (amount, native, opts) => {
      if (amount == null || !Number.isFinite(amount)) return "—";
      const tag = intlTag(locale);
      const compact = opts?.compact ?? false;
      const nativeCcy = native ?? undefined;

      // Native currency (default), or no display override → format as-is.
      if (!displayCurrency || !nativeCcy || displayCurrency === nativeCcy) {
        return formatAmount(amount, nativeCcy, tag, compact);
      }

      // Convert to the display currency via the FX rate as-of the value's date.
      const asOf = opts?.asOf ?? new Date().toISOString().slice(0, 10);
      const rate = fxRate(nativeCcy, displayCurrency, asOf);
      if (rate == null) {
        // No rate available — show native, never coerce 1:1.
        return formatAmount(amount, nativeCcy, tag, compact);
      }
      const converted = applyFx(amount, rate);
      return converted == null ? "—" : formatAmount(converted, displayCurrency, tag, compact);
    },
    [locale, displayCurrency],
  );

  return { displayCurrency, money };
}
